import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, useNavigate } from "@tanstack/react-router"
import axios from "axios"
import { Badge } from "design-system/atoms/Badge"
import { Button } from "design-system/atoms/Button"
import { Input } from "design-system/atoms/Input"
import { Label } from "design-system/atoms/Label"
import { Textarea } from "design-system/atoms/Textarea"
import { Tabs, TabsList, TabsTrigger } from "design-system/molecules/Tabs"
import { FileSearch, Loader2 } from "lucide-react"
import { useId } from "react"
import { useForm, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import {
  DataResponse,
  MANUAL_KNOWLEDGE_SUGGESTION_STATUSES,
  PublishKnowledgeSuggestionResponseDataSchema,
  PublishKnowledgeSuggestionSchema,
} from "schema"
import { z } from "zod"
import { knowledgeBaseQuery } from "../lib/knowledgeBaseQuery"
import { KnowledgeSuggestion, knowledgeSuggestionsQuery } from "../lib/knowledgeSuggestionsQuery"
import { KNOWLEDGE_SUGGESTION_TABS, KnowledgeSuggestionTab } from "../lib/knowledgeSuggestionTabs"
import {
  getShouldSkipDismissConfirmation,
  useDismissKnowledgeSuggestionMutation,
} from "../lib/useDismissKnowledgeSuggestionMutation"

const MANUAL_ENTRY_STATUSES = new Set<KnowledgeSuggestion["status"]>(
  MANUAL_KNOWLEDGE_SUGGESTION_STATUSES,
)
const READY_TAB_STATUSES = new Set<KnowledgeSuggestion["status"]>(["ready", "published"])

type Props = {
  tab: KnowledgeSuggestionTab
  onChangeTab: (tab: KnowledgeSuggestionTab) => void
}

export function KnowledgeSuggestions({ tab, onChangeTab }: Props) {
  const { t } = useTranslation()
  const { data, isPending, isError, refetch } = useQuery(knowledgeSuggestionsQuery())
  if (isPending)
    return (
      <p role="status" className="text-muted-foreground">
        {t("knowledge.suggestions.loading")}
      </p>
    )
  if (isError)
    return (
      <div role="alert" className="space-y-3">
        <p>{t("knowledge.suggestions.loadError")}</p>
        <Button variant="outline" onClick={() => refetch()}>
          {t("knowledge.suggestions.retry")}
        </Button>
      </div>
    )
  if (!data.length)
    return <p className="text-muted-foreground">{t("knowledge.suggestions.empty")}</p>

  const suggestionsByTab = {
    ready: data.filter(({ status }) => READY_TAB_STATUSES.has(status)),
    noEvidence: data.filter(({ status }) => !READY_TAB_STATUSES.has(status)),
  }
  const suggestions = suggestionsByTab[tab]

  return (
    <Tabs
      value={tab}
      onValueChange={(value) => onChangeTab(value as KnowledgeSuggestionTab)}
      className="flex flex-col gap-4"
    >
      <TabsList>
        {KNOWLEDGE_SUGGESTION_TABS.map((value) => (
          <TabsTrigger key={value} value={value}>
            {t(`knowledge.suggestions.tabs.${value}`, { count: suggestionsByTab[value].length })}
          </TabsTrigger>
        ))}
      </TabsList>
      {suggestions.length ? (
        <div className="space-y-4">
          {suggestions.map((suggestion) => (
            <SuggestionCard
              key={`${suggestion.id}:${suggestion.suggestedAt}`}
              suggestion={suggestion}
            />
          ))}
        </div>
      ) : (
        <p className="text-muted-foreground">{t("knowledge.suggestions.emptyTab")}</p>
      )}
    </Tabs>
  )
}

function SuggestionCard({ suggestion }: { suggestion: KnowledgeSuggestion }) {
  const { t } = useTranslation()
  const proposal = suggestion.result?.proposal
  const ticketCount = new Set(proposal?.sources.map(({ externalTicketId }) => externalTicketId))
    .size
  const hasEditor = suggestion.status === "ready" || MANUAL_ENTRY_STATUSES.has(suggestion.status)

  return (
    <article className="space-y-4 rounded-lg border border-border bg-card p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h2 className="font-semibold">{suggestion.question}</h2>
          <p className="text-sm text-muted-foreground">
            {t("knowledge.suggestions.frequency", {
              count: suggestion.sampledQuestionCount,
              category: suggestion.category ?? t("knowledge.suggestions.noCategory"),
            })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {suggestion.status === "ready" && proposal ? (
            <Button variant="outline" size="sm" asChild>
              <Link
                to="/knowledge/suggestions/$gapId/evidence"
                params={{ gapId: suggestion.id }}
                search={{ tab: "ready" }}
              >
                <FileSearch aria-hidden="true" className="h-4 w-4" />
                {t("knowledge.suggestions.viewEvidence", { count: ticketCount })}
              </Link>
            </Button>
          ) : (
            <Badge variant="secondary">
              {t(`knowledge.suggestions.status.${suggestion.status}`)}
            </Badge>
          )}
        </div>
      </div>
      {suggestion.status === "pending" && (
        <p role="status" className="text-sm text-muted-foreground">
          {t("knowledge.suggestions.pending")}
        </p>
      )}
      {hasEditor ? (
        <SuggestionEditor suggestion={suggestion} isSuggested={suggestion.status === "ready"} />
      ) : (
        <SuggestionAnalysis suggestion={suggestion} />
      )}
      {suggestion.status === "pending" && <DismissButton suggestion={suggestion} />}
      {suggestion.status === "published" && suggestion.publishedKnowledgeBaseItemId && (
        <Button variant="outline" asChild>
          <Link
            to="/knowledge/knowledge-base"
            search={{
              section: "QuestionAndAnswer",
              highlightId: suggestion.publishedKnowledgeBaseItemId,
            }}
          >
            {t("knowledge.suggestions.viewEntry")}
          </Link>
        </Button>
      )}
    </article>
  )
}

function DismissButton({ suggestion }: { suggestion: KnowledgeSuggestion }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { mutate, isPending, isError } = useDismissKnowledgeSuggestionMutation()

  function onDismiss() {
    if (getShouldSkipDismissConfirmation()) return mutate(suggestion.id)

    navigate({
      to: "/knowledge/suggestions/$gapId/dismiss",
      params: { gapId: suggestion.id },
      search: { tab: READY_TAB_STATUSES.has(suggestion.status) ? "ready" : "noEvidence" },
    })
  }

  return (
    <Button
      type="button"
      variant="outline"
      disabled={isPending}
      title={isError ? t("knowledge.suggestions.dismissError") : undefined}
      onClick={onDismiss}
    >
      {t("knowledge.suggestions.dismiss")}
    </Button>
  )
}

export function SuggestionAnalysis({ suggestion }: { suggestion: KnowledgeSuggestion }) {
  const { t } = useTranslation()
  if (!suggestion.result?.reason) return null

  return (
    <p className="text-sm text-muted-foreground">
      <span className="font-medium text-foreground">{t("knowledge.suggestions.analysis")}</span>
      {": "}
      {suggestion.result.reason}
    </p>
  )
}

function SuggestionEditor({
  suggestion,
  isSuggested,
}: {
  suggestion: KnowledgeSuggestion
  isSuggested: boolean
}) {
  const { t } = useTranslation()
  const formId = useId()
  const queryClient = useQueryClient()
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<z.infer<typeof PublishKnowledgeSuggestionSchema>>({
    resolver: zodResolver(PublishKnowledgeSuggestionSchema),
    defaultValues: {
      question: suggestion.result?.proposal?.question ?? suggestion.question,
      answer: suggestion.result?.proposal?.answer ?? "",
    },
  })
  const [question, answer] = useWatch({ control, name: ["question", "answer"] })
  const hasContent = Boolean(question.trim() && answer.trim())
  const { mutate, isPending, isError } = useMutation({
    mutationFn: async (values: z.infer<typeof PublishKnowledgeSuggestionSchema>) => {
      const { data } = await axios.post<
        DataResponse<z.infer<typeof PublishKnowledgeSuggestionResponseDataSchema>>
      >(`/knowledgeBase/suggestions/${suggestion.id}/publish`, values)
      return data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: knowledgeSuggestionsQuery().queryKey })
      queryClient.invalidateQueries({ queryKey: [knowledgeBaseQuery().queryKey.at(0)] })
    },
  })

  return (
    <form
      onSubmit={handleSubmit((values) => mutate(values))}
      className="space-y-3 border-t border-border pt-4"
    >
      <p className="text-sm text-muted-foreground">
        {t(getEditorHelpKey(suggestion, isSuggested))}
      </p>
      <div className="space-y-2">
        <Label htmlFor={`${formId}-question`}>{t("knowledge.suggestions.question")}</Label>
        <Input
          id={`${formId}-question`}
          {...register("question")}
          disabled={isPending}
          aria-invalid={!!errors.question}
          aria-describedby={errors.question ? `${formId}-question-error` : undefined}
        />
        {errors.question && (
          <p id={`${formId}-question-error`} role="alert" className="text-sm text-destructive">
            {t("knowledge.suggestions.invalidQuestion")}
          </p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${formId}-answer`}>
          {t(
            isSuggested ? "knowledge.suggestions.suggestedAnswer" : "knowledge.suggestions.answer",
          )}
        </Label>
        <Textarea
          id={`${formId}-answer`}
          {...register("answer")}
          rows={7}
          placeholder={isSuggested ? undefined : t("knowledge.suggestions.answerPlaceholder")}
          disabled={isPending}
          aria-invalid={!!errors.answer}
          aria-describedby={errors.answer ? `${formId}-answer-error` : undefined}
        />
        {errors.answer && (
          <p id={`${formId}-answer-error`} role="alert" className="text-sm text-destructive">
            {t("knowledge.suggestions.invalidAnswer")}
          </p>
        )}
      </div>
      <SuggestionAnalysis suggestion={suggestion} />
      {isError && (
        <p role="alert" className="text-sm text-destructive">
          {t("knowledge.suggestions.publishError")}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={isPending || !hasContent}>
          {isPending && <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />}
          {t("knowledge.suggestions.publish")}
        </Button>
        <DismissButton suggestion={suggestion} />
      </div>
    </form>
  )
}

function getEditorHelpKey(suggestion: KnowledgeSuggestion, isSuggested: boolean) {
  if (isSuggested) return "knowledge.suggestions.review"
  if (suggestion.status === "covered") return "knowledge.suggestions.manualCoveredHelp"

  return "knowledge.suggestions.manualHelp"
}
